import styled, { css } from 'vue-styled-components';

import theme from '@/config/theme';

export const Icon = ['', 'search', 'eye'] as const;
export const Pattern = ['default', 'primary'] as const;
export const Type = ['text', 'password', 'date', 'email'] as const;

const wrapperProps = {
  error: Boolean,
};

const inputProps = {
  icon: String,
  pattern: String,
};

export type WrapperProps = {
  error: boolean;
}

export type InputProps = {
  icon: string;
  pattern: typeof Pattern[number];
};

const modifiers = {
  default: {
    bg: theme.colors.white,
    border: `1px solid ${theme.colors.gray4}`,
    borderRadius: `${theme.border.radius.xsmall}`,
    color: theme.colors.black,
    fontWeight: theme.font.regular,
    focus: {
      bg: theme.colors.white,
      border: theme.colors.primary,
      fontWeight: theme.font.regular,
      placeholder: theme.colors.black,
    },
    placeholder: theme.colors.gray4,
  },
  primary: {
    bg: 'rgba(255, 255, 255, 0.25)',
    border: 'none',
    borderRadius: `${theme.border.radius.xsmall}`,
    color: theme.colors.primary,
    fontWeight: theme.font.bold,
    focus: {
      bg: 'rgba(255, 255, 255, 0.5)',
      border: 'none',
      fontWeight: theme.font.bold,
      placeholder: theme.colors.primary,
    },
    placeholder: theme.colors.white,
  },
};

export const StyledInput = styled('input', inputProps)`
  ${({ icon, pattern }: InputProps) => css`
    background: ${modifiers[pattern].bg};
    border: ${modifiers[pattern].border};
    border-radius: ${modifiers[pattern].borderRadius};
    color: ${modifiers[pattern].color};
    font-size: ${theme.font.sizes.p2};
    font-weight: ${modifiers[pattern].fontWeight};
    height: 48px;
    line-height: ${theme.font.lineHeights.p2};
    padding: ${theme.spacings.xsmall} ${theme.spacings.medium};
    ${icon && 'padding-right: 44px'};
    width: 100%;

    &::placeholder {
      color: ${modifiers[pattern].placeholder};
      font-weight: ${theme.font.regular};
    }

    &:hover, &:focus {
      background: ${modifiers[pattern].focus.bg};
      border-color: ${modifiers[pattern].focus.border};
    }

    &:focus, &.active {
      background: ${theme.colors.white};
      font-weight: ${modifiers[pattern].focus.fontWeight};
    }

    & ~ div {
      display: none;
    }

    &:hover ~ div {
      display: block;
      position: absolute;
      right: 0;
      top: calc(100% + 10px);
      z-index: ${theme.layers.base};
    }

    &:focus ~ a, &.active ~ a {
      color:  ${theme.colors.gray5} !important;
    }

    &:focus::placeholder {
      color: ${modifiers[pattern].focus.placeholder};
    }

    &:focus~label:first-of-type {
      align-items: center;
      color: ${theme.colors.primary};
      margin-top: 0;
      opacity: 1;
      top: -24px;
    }

    &.small {
      border-radius: ${theme.border.radius.small};
      border-width: 2px;
      height: 40px;

      &:focus~label {
        top: -20px;
      }
    }

    &.error {
      border-color: ${theme.colors.supportError};

      &:focus~label:first-of-type {
        color: ${theme.colors.supportError};
      }
    }
  `}
`;

export const StyledLabel = styled.label`
  align-items: center;
  display: flex;
  font-size: ${theme.font.sizes.p3};
  height: 24px;
  left: 0px;
  margin-top: 0;
  opacity: 0;
  padding: 0 ${theme.spacings.medium};
  pointer-events: none;
  position: absolute;
  top: -24px;
  transition: all 0.3s;

  &.primary {
    color: ${theme.colors.white};
  }

  &.active {
    color: ${theme.colors.gray5};
    opacity: 1;
  }

  &.error {
    color: ${theme.colors.supportError};
  }
`;

export const StyledLabelError = styled('label')`
  color: ${theme.colors.supportError};
  font-size: ${theme.font.sizes.p3};
  margin-top: 5px;
  position: absolute;
  right: 0;
  top: 100%;
`;

export const WrapperIcon = styled.a`
  background-position: center;
  background-repeat: no-repeat;
  display: flex;
  max-height: 20px;
  max-width: 20px;
  position: absolute;
  right: ${theme.spacings.medium};
`;

export const WrapperInput = styled('div', wrapperProps)`
  ${({ error }: WrapperProps) => css`
    align-items: center;
    display: flex;
    position: relative;
    width: 100%;

    ${error && `margin-bottom: ${theme.spacings.xlarge}`};
  `}
`;
